import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, MenuItem, Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isValid, subDays } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CoffeeIcon from '@mui/icons-material/Coffee';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pulseRequest } from '../utils/pulseRequest';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const ET_OPTS = { timeZone: 'America/New_York' };

// ── helpers ───────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { ...ET_OPTS, hour: 'numeric', minute: '2-digit' });
}

function formatMinutes(min) {
  if (!min && min !== 0) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// status is 'clocked_in' | 'on_break' | 'clocked_out'. When on_break, breakType
// ('lunch' | 'break') selects the label/color so lunch and break read distinctly.
function statusChip(status, breakType, categoryLabel) {
  if (status === 'clocked_in') {
    const label = categoryLabel ? `Checked In — ${categoryLabel}` : 'Checked In';
    return <Chip label={label} size="small" sx={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 600 }} />;
  }
  if (status === 'on_break') {
    if (breakType === 'lunch') return <Chip label="On Lunch" size="small" sx={{ backgroundColor: '#7b1fa2', color: '#fff', fontWeight: 600 }} />;
    return <Chip label="On Break" size="small" sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600 }} />;
  }
  return <Chip label="Checked Out" size="small" variant="outlined" />;
}

// Quote a CSV cell if it contains comma/quote/newline.
function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Client-side CSV download — matches the Reports.js downloadCSV pattern.
function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Column totals for the Activity Report tables, computed from the exact rows
// the tables render — auto-closed exclusions are already baked into each row's
// values upstream, so totals always reconcile with what's on screen.
function computeReportTotals(rows, cats) {
  const sum = (fn) => rows.reduce((acc, r) => acc + (Number(fn(r)) || 0), 0);
  const totals = {
    sessions: sum(r => r.session_count),
    total: sum(r => r.total_clocked_minutes),
    lunch: sum(r => r.lunch_minutes),
    brk: sum(r => r.break_minutes),
    net: sum(r => r.net_clocked_minutes),
    picking: sum(r => r.picking_minutes),
    gap: sum(r => r.gap_minutes),
    unallocated: sum(r => r.unallocated_minutes),
    autoClosed: sum(r => r.auto_closed_count),
    categories: {},
  };
  cats.forEach(c => { totals.categories[c.key] = sum(r => r.category_minutes?.[c.key]); });
  // Breakdown denominator: allocated + unallocated, so that row sums to 100%.
  totals.breakdownDenom = Object.values(totals.categories).reduce((a, b) => a + b, 0) + totals.unallocated;
  return totals;
}

// "3h 8m (42%)" — whole-number percentage; a zero denominator (empty range)
// renders the time alone, never NaN/Infinity.
function withPct(minutes, denom) {
  return denom > 0 ? `${formatMinutes(minutes)} (${Math.round((minutes / denom) * 100)}%)` : formatMinutes(minutes);
}

// CSV flavor of the same: raw minutes + percentage, e.g. "188 (42%)".
function csvWithPct(minutes, denom) {
  const m = Math.round(minutes * 10) / 10;
  return denom > 0 ? `${m} (${Math.round((minutes / denom) * 100)}%)` : String(m);
}

// Default report range: yesterday 3 PM → yesterday 11 PM (night shift since Nov 2 2026)
function getDefaultReportRange() {
  const yesterday = subDays(new Date(), 1);
  const start = new Date(yesterday);
  start.setHours(15, 0, 0, 0);
  const end = new Date(yesterday);
  end.setHours(23, 0, 0, 0);
  return { start, end };
}

// ── component ─────────────────────────────────────────────────────────────────

const ClockIn = () => {
  const { canAccessManagerFeatures, currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();

  // 401 from any kiosk request → the tablet's login session has expired.
  // Full-screen re-login state instead of per-tap red snackbars.
  const [authExpired, setAuthExpired] = useState(false);

  // Kiosk
  const [empId, setEmpId]                   = useState('');
  const [lookupLoading, setLookupLoading]   = useState(false);
  const [currentStatus, setCurrentStatus]   = useState(null); // { status, session, worker_name }
  const [actionLoading, setActionLoading]   = useState(false);

  // Live roster
  const [roster, setRoster]                 = useState([]);
  const [rosterLoading, setRosterLoading]   = useState(false);

  // Live timer tick
  const [currentTime, setCurrentTime]       = useState(new Date());

  // Manager force actions — worker selected by NAME (resolves to employee_id;
  // the force-in/force-out endpoints are unchanged and still receive the ID)
  const [workers, setWorkers]               = useState([]); // active obd_workers with an employee_id
  const [forceWorker, setForceWorker]       = useState(null);
  const [forceCategoryKey, setForceCategoryKey] = useState(''); // '' = No category (default)
  const [forceLoading, setForceLoading]     = useState(false);
  // Lead OBD pause/resume: the selected worker's In Progress / Paused OBDs
  const [forceWorkerObds, setForceWorkerObds] = useState([]);

  // Reports
  const defaults = getDefaultReportRange();
  const [reportStart, setReportStart]       = useState(defaults.start);
  const [reportEnd, setReportEnd]           = useState(defaults.end);
  const [reportData, setReportData]         = useState(null);
  const [reportLoading, setReportLoading]   = useState(false);

  // Clock Out All (manager/admin, password-gated)
  const [clockOutAllOpen, setClockOutAllOpen]   = useState(false);
  const [clockOutUsername, setClockOutUsername] = useState('');
  const [clockOutPassword, setClockOutPassword] = useState('');
  const [clockOutLoading, setClockOutLoading]   = useState(false);
  const [clockOutError, setClockOutError]       = useState('');

  // Clock-in nag pilot toggle (Change 6, admin-only control)
  const [nagEnabled, setNagEnabled]         = useState(false);
  const [nagSaving, setNagSaving]           = useState(false);

  // Auto check-in-to-Picking on OBD claim (admin-only control, default ON)
  const [autoPickingEnabled, setAutoPickingEnabled] = useState(true);
  const [autoPickingSaving, setAutoPickingSaving]   = useState(false);

  // Activity categories (labels always come from the API, never hardcoded)
  const [categories, setCategories]         = useState([]); // [{ key, label }]

  // Optional vendor/PO tagging for Receiving/Putaway check-ins (metadata only —
  // completely separate from Putaway Sign On's truck timers)
  const [vendors, setVendors]               = useState([]); // active putaway_vendors
  const [vendorDialog, setVendorDialog]     = useState({ open: false, mode: null, catKey: null, catLabel: '' });
  const [vendorSel, setVendorSel]           = useState('');
  const [poText, setPoText]                 = useState('');

  // Feedback
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success' });

  // ── timer tick every second for live elapsed display ────────────────────────
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── fetch live roster ────────────────────────────────────────────────────────
  const fetchRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/active`, { headers: getAuthHeaders() });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (r.ok) setRoster(r.data);
      else console.error('[clock] roster fetch error:', r.message);
    } finally {
      setRosterLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
    const t = setInterval(fetchRoster, 30000); // auto-refresh every 30 s
    return () => clearInterval(t);
  }, [fetchRoster]);

  // Load active activity categories once (kiosk check-in buttons + report columns).
  useEffect(() => {
    pulseRequest(`${API_BASE}/api/clock/categories`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.sessionExpired) { setAuthExpired(true); return; }
        setCategories(r.ok && Array.isArray(r.data) ? r.data : []);
      });
    // Vendors for the optional Receiving/Putaway tag dialog — reuses the
    // existing Putaway vendors endpoint (returns active vendors only).
    pulseRequest(`${API_BASE}/api/putaway/vendors`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.sessionExpired) { setAuthExpired(true); return; }
        setVendors(r.ok && Array.isArray(r.data) ? r.data : []);
      });
  }, []);

  // Active workers for the Manager Override name lookup — same endpoint the
  // OBD Sign-On "Select Your Name" dropdown uses (active-only by default).
  // Workers without an employee_id are excluded: force actions require the ID.
  useEffect(() => {
    if (!canAccessManagerFeatures) return;
    pulseRequest(`${API_BASE}/api/obd/workers`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.sessionExpired) { setAuthExpired(true); return; }
        setWorkers((r.ok && Array.isArray(r.data) ? r.data : []).filter(w => w.employee_id && String(w.employee_id).trim()));
      });
  }, [canAccessManagerFeatures]);

  // Load the nag pilot toggle (admins see and control it on this page).
  useEffect(() => {
    if (!isAdmin) return;
    pulseRequest(`${API_BASE}/api/clock/nag-config`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.sessionExpired) { setAuthExpired(true); return; }
        setNagEnabled(r.ok && r.data ? r.data.enabled === true : false);
      });
    pulseRequest(`${API_BASE}/api/clock/auto-picking-config`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.sessionExpired) { setAuthExpired(true); return; }
        setAutoPickingEnabled(r.ok && r.data ? r.data.enabled !== false : true);
      });
  }, [isAdmin]);

  const handleToggleAutoPicking = async (next) => {
    setAutoPickingSaving(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/auto-picking-config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled: next }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      setAutoPickingEnabled(r.data.enabled === true);
      setSnackbar({ open: true, message: `Auto check-in on OBD claim ${r.data.enabled ? 'enabled' : 'disabled'}.`, severity: 'success' });
    } finally {
      setAutoPickingSaving(false);
    }
  };

  const handleToggleNag = async (next) => {
    setNagSaving(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/nag-config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled: next }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      setNagEnabled(r.data.enabled === true);
      setSnackbar({ open: true, message: `Check-in reminder ${r.data.enabled ? 'enabled' : 'disabled'}.`, severity: 'success' });
    } finally {
      setNagSaving(false);
    }
  };

  // ── kiosk: look up employee status by ID ────────────────────────────────────
  const handleLookup = async (idOverride) => {
    const id = (idOverride || empId).trim();
    if (!id) return;
    setLookupLoading(true);
    setCurrentStatus(null);
    try {
      const r = await pulseRequest(
        `${API_BASE}/api/clock/status?employee_id=${encodeURIComponent(id)}`,
        { headers: getAuthHeaders() }
      );
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      // /status now resolves the worker from obd_workers and returns found +
      // worker_name directly, so a never-clocked-in employee still shows up.
      setCurrentStatus({
        ...r.data,
        worker_name: r.data.worker_name || null,
        employee_id: id,
      });
    } finally {
      setLookupLoading(false);
    }
  };

  // ── kiosk: perform an action (out / break-start / break-end) ─────────────────
  // For break/start, pass breakType ('break' | 'lunch').
  const handleAction = async (endpoint, breakType) => {
    const id = empId.trim();
    if (!id) return;
    setActionLoading(true);
    try {
      const body = { employee_id: id };
      if (endpoint === 'break/start') body.break_type = breakType || 'break';
      const r = await pulseRequest(`${API_BASE}/api/clock/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        // 409 = already checked in — handle gracefully
        if (r.status === 409 && data.worker_name) {
          setCurrentStatus({ status: data.status, session: null, worker_name: data.worker_name, employee_id: id });
          setSnackbar({ open: true, message: r.message, severity: 'warning' });
          await handleLookup(id);
          return;
        }
        // Any other 4xx (stale-tap class): friendly message, then re-run the
        // lookup so stale buttons never persist under an error.
        if (r.status >= 400 && r.status < 500) {
          setSnackbar({ open: true, message: r.message, severity: 'warning' });
          await handleLookup(id);
          fetchRoster();
          return;
        }
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      // Self-healing break/start: already on break → neutral notice, no error.
      if (endpoint === 'break/start' && data.already_on_break) {
        setSnackbar({ open: true, message: "You're already on break/lunch", severity: 'info' });
        await handleLookup(id);
        return;
      }
      const msgs = {
        'out':         `Checked out: ${data.session?.worker_name || ''}`,
        'break/start': (breakType === 'lunch' ? 'Lunch' : 'Break') + ' started',
        'break/end':   'Break ended — activity resumed',
      };
      setSnackbar({ open: true, message: msgs[endpoint] || 'Done', severity: 'success' });
      // Refresh status display and roster
      await handleLookup(id);
      fetchRoster();
    } finally {
      setActionLoading(false);
    }
  };

  // ── kiosk: check in under a category (one tap = check-in + open activity) ────
  // Tap interceptors: Receiving/Putaway ('receiving' key — stable identifier)
  // gets a small OPTIONAL vendor/PO dialog first; every other category goes
  // straight through. Tapping the CURRENT category never opens the dialog
  // (the friendly no-op wins).
  const tapCheckIn = (cat) => {
    if (cat.key === 'receiving') {
      setVendorSel(''); setPoText('');
      setVendorDialog({ open: true, mode: 'checkin', catKey: cat.key, catLabel: cat.label });
      return;
    }
    handleCheckIn(cat.key);
  };

  const tapSwitch = (cat) => {
    if (currentStatus?.activity?.category_key === cat.key) {
      handleSwitchCategory(cat.key, cat.label); // local no-op path ("Already in X")
      return;
    }
    if (cat.key === 'receiving') {
      setVendorSel(''); setPoText('');
      setVendorDialog({ open: true, mode: 'switch', catKey: cat.key, catLabel: cat.label });
      return;
    }
    handleSwitchCategory(cat.key, cat.label);
  };

  // Dialog completion: Skip → null tags; Confirm → whatever was entered
  // (empty selections behave exactly like Skip). Never blocks the check-in.
  const completeVendorDialog = (withTags) => {
    const { mode, catKey, catLabel } = vendorDialog;
    const tags = withTags
      ? { ...(vendorSel ? { vendor_id: vendorSel } : {}), ...(poText.trim() ? { po_number: poText.trim() } : {}) }
      : {};
    setVendorDialog({ open: false, mode: null, catKey: null, catLabel: '' });
    if (mode === 'checkin') handleCheckIn(catKey, tags);
    else handleSwitchCategory(catKey, catLabel, tags);
  };

  const handleCheckIn = async (categoryKey, tags = {}) => {
    const id = empId.trim();
    if (!id) return;
    setActionLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/in`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_id: id, category_key: categoryKey, ...tags }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        if (r.status === 409 && data.worker_name) {
          setCurrentStatus({ status: data.status, session: null, worker_name: data.worker_name, employee_id: id });
          setSnackbar({ open: true, message: r.message, severity: 'warning' });
          await handleLookup(id);
          return;
        }
        if (r.status >= 400 && r.status < 500) {
          setSnackbar({ open: true, message: r.message, severity: 'warning' });
          await handleLookup(id);
          fetchRoster();
          return;
        }
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: `Checked in: ${data.worker_name} — ${data.category?.label || ''}`, severity: 'success' });
      await handleLookup(id);
      fetchRoster();
    } finally {
      setActionLoading(false);
    }
  };

  // ── kiosk: switch activity category (one tap, close old + open new) ──────────
  const handleSwitchCategory = async (categoryKey, categoryLabel, tags = {}) => {
    const id = empId.trim();
    if (!id) return;
    // Tapping the CURRENT category: friendly no-op, no server round-trip needed.
    if (currentStatus?.activity?.category_key === categoryKey) {
      setSnackbar({ open: true, message: `Already in ${categoryLabel}`, severity: 'info' });
      return;
    }
    setActionLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/switch-category`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_id: id, category_key: categoryKey, ...tags }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        if (r.status >= 400 && r.status < 500) {
          // e.g. "On break/lunch — end the break before switching activity."
          setSnackbar({ open: true, message: r.message, severity: 'warning' });
          await handleLookup(id);
          fetchRoster();
          return;
        }
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      if (data.noop) {
        setSnackbar({ open: true, message: data.message || `Already in ${categoryLabel}`, severity: 'info' });
      } else {
        setSnackbar({ open: true, message: `Now in ${data.category?.label || categoryLabel}`, severity: 'success' });
      }
      await handleLookup(id);
      fetchRoster();
    } finally {
      setActionLoading(false);
    }
  };

  // ── Clock Out All (manager/admin, password-gated) ────────────────────────────
  const handleClockOutAll = async () => {
    if (!clockOutUsername.trim() || !clockOutPassword) {
      setClockOutError('Username and password are required.');
      return;
    }
    setClockOutLoading(true);
    setClockOutError('');
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/clock-out-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username: clockOutUsername.trim(), password: clockOutPassword }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setClockOutError(r.message);
        return;
      }
      setClockOutAllOpen(false);
      setClockOutUsername('');
      setClockOutPassword('');
      setSnackbar({ open: true, message: `Checked out ${r.data.cleared} associate(s).`, severity: 'success' });
      fetchRoster();
    } finally {
      setClockOutLoading(false);
    }
  };

  // ── CSV export of the gap report (Change 4) ──────────────────────────────────
  // Includes the per-category activity breakdown + Unallocated (labels from the API).
  const handleExportReportCSV = () => {
    if (!reportData || reportData.length === 0) return;
    const headers = [
      'Associate', 'Employee ID', 'Sessions', 'Total Checked-In (min)', 'Lunch (min)', 'Break (min)',
      'Net Checked-In (min)', 'Picking (min)', 'Gap (min)', 'Auto-Closed (excluded)',
      ...categories.map(c => `${c.label} (min)`),
      'Unallocated (min)',
    ];
    const lines = [headers.map(csvCell).join(',')];
    reportData.forEach(r => {
      lines.push([
        r.worker_name, r.employee_id, r.session_count,
        r.total_clocked_minutes, r.lunch_minutes, r.break_minutes,
        r.net_clocked_minutes, r.picking_minutes, r.gap_minutes,
        r.auto_closed_count || 0,
        ...categories.map(c => r.category_minutes?.[c.key] ?? 0),
        r.unallocated_minutes ?? 0,
      ].map(csvCell).join(','));
    });
    // Totals row mirroring the on-screen totals: Lunch/Break/Net as % of Total
    // Checked-In, Picking/Gap as % of Net, breakdown columns as % of
    // (allocated + unallocated). Values are minutes, like the data rows.
    const t = computeReportTotals(reportData, categories);
    lines.push([
      'TOTAL', '', t.sessions,
      Math.round(t.total * 10) / 10,
      csvWithPct(t.lunch, t.total), csvWithPct(t.brk, t.total),
      csvWithPct(t.net, t.total), csvWithPct(t.picking, t.net), csvWithPct(t.gap, t.net),
      t.autoClosed,
      ...categories.map(c => csvWithPct(t.categories[c.key] || 0, t.breakdownDenom)),
      csvWithPct(t.unallocated, t.breakdownDenom),
    ].map(csvCell).join(','));
    const stamp = `${reportStart.toISOString().slice(0,10)}_to_${reportEnd.toISOString().slice(0,10)}`;
    downloadCSV(lines.join('\n'), `activity-report-${stamp}.csv`);
  };

  // ── manager force actions ─────────────────────────────────────────────────────
  // Force-in optionally carries a category_key: chosen → the activity opens too;
  // left as "No category" → today's behavior (roster shows "—").
  // ── lead OBD pause/resume ────────────────────────────────────────────────────
  // Pause is lead-only (client request): associates ask a lead, who pauses from
  // here. Resume is offered too so a lead can un-pause without the associate.
  const fetchForceWorkerObds = useCallback(async (workerName) => {
    if (!workerName) { setForceWorkerObds([]); return; }
    const r = await pulseRequest(`${API_BASE}/api/obd/all`, { headers: getAuthHeaders() });
    setForceWorkerObds((r.ok && Array.isArray(r.data) ? r.data : []).filter(o =>
      o.assigned_worker === workerName && ['In Progress', 'Paused'].includes(o.status)));
  }, []);

  useEffect(() => {
    fetchForceWorkerObds(forceWorker?.name);
  }, [forceWorker, fetchForceWorkerObds]);

  const handleObdPauseResume = async (obd, action) => {
    setForceLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/obd/${obd.id}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'warning' });
      } else if (data.already_paused) {
        setSnackbar({ open: true, message: `OBD ${obd.obd_number} was already paused`, severity: 'info' });
      } else {
        setSnackbar({
          open: true,
          message: `OBD ${obd.obd_number} ${action === 'pause' ? 'paused' : 'resumed'} for ${forceWorker?.name || obd.assigned_worker}`,
          severity: 'success',
        });
      }
      fetchForceWorkerObds(forceWorker?.name);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setForceLoading(false);
    }
  };

  const handleForce = async (action) => {
    const id = forceWorker?.employee_id ? String(forceWorker.employee_id).trim() : '';
    if (!id) return;
    setForceLoading(true);
    try {
      const body = { employee_id: id };
      if (action === 'force-in' && forceCategoryKey) body.category_key = forceCategoryKey;
      const r = await pulseRequest(`${API_BASE}/api/clock/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      const data = r.data || {};
      const suffix = action === 'force-in' && data.category?.label ? ` — ${data.category.label}` : '';
      setSnackbar({ open: true, message: `${action === 'force-in' ? 'Force Activity Check In' : 'Force Activity Check Out'} done for ${data.worker_name || forceWorker.name}${suffix}`, severity: 'success' });
      setForceWorker(null);
      setForceCategoryKey('');
      fetchRoster();
    } finally {
      setForceLoading(false);
    }
  };

  // ── lead force-assign lunch/break ────────────────────────────────────────────
  // Contextual on the selected worker's live roster status. Forced breaks use
  // standard break semantics and are indistinguishable from self-logged ones
  // in every report.
  const handleForceBreak = async (breakType) => {
    const id = forceWorker?.employee_id ? String(forceWorker.employee_id).trim() : '';
    if (!id) return;
    setForceLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/force-break`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_id: id, break_type: breakType }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'warning' });
        fetchRoster();
        return;
      }
      setSnackbar({ open: true, message: `${breakType === 'lunch' ? 'Lunch' : 'Break'} assigned to ${data.worker_name || forceWorker.name}`, severity: 'success' });
      setForceWorker(null);
      setForceCategoryKey('');
      fetchRoster();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setForceLoading(false);
    }
  };

  const handleForceBreakEnd = async () => {
    const id = forceWorker?.employee_id ? String(forceWorker.employee_id).trim() : '';
    if (!id) return;
    setForceLoading(true);
    try {
      const r = await pulseRequest(`${API_BASE}/api/clock/force-break-end`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ employee_id: id }),
      });
      if (r.sessionExpired) { setAuthExpired(true); return; }
      const data = r.data || {};
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'warning' });
        fetchRoster();
        return;
      }
      setSnackbar({ open: true, message: `Break ended for ${data.worker_name || forceWorker.name}`, severity: 'success' });
      setForceWorker(null);
      setForceCategoryKey('');
      fetchRoster();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setForceLoading(false);
    }
  };

  // ── report fetch ──────────────────────────────────────────────────────────────
  const fetchReport = async () => {
    if (!isValid(reportStart) || !isValid(reportEnd)) {
      setSnackbar({ open: true, message: 'Invalid date range', severity: 'error' });
      return;
    }
    setReportLoading(true);
    try {
      const r = await pulseRequest(
        `${API_BASE}/api/clock/report?startDate=${reportStart.toISOString()}&endDate=${reportEnd.toISOString()}`,
        { headers: getAuthHeaders() }
      );
      if (r.sessionExpired) { setAuthExpired(true); return; }
      if (!r.ok) {
        setSnackbar({ open: true, message: r.message, severity: 'error' });
        return;
      }
      setReportData(r.data);
    } finally {
      setReportLoading(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────

  const status = currentStatus?.status || 'clocked_out';

  // Tablet session expired → full-screen re-login state. Every kiosk action
  // would 401; a clear instruction beats a stream of red snackbars.
  if (authExpired) {
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
        <Paper sx={{ p: 5 }}>
          <AccessTimeIcon sx={{ fontSize: 64, color: '#f57c00', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            This tablet needs to be signed in again
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The login session has expired. Please ask a lead to sign the tablet back in — check-ins and breaks will work again right after.
          </Typography>
          <Button variant="contained" size="large" startIcon={<LoginIcon />} onClick={() => navigate('/login')}>
            Go to Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 3, mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon fontSize="large" />
          PULSE Activity Monitor
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Operational visibility only — not connected to payroll. WFM remains the system of record.
        </Typography>

        <Grid container spacing={3}>

          {/* ── Kiosk card ──────────────────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoginIcon /> Activity Check In / Out
              </Typography>

              <TextField
                label="Employee ID"
                fullWidth
                value={empId}
                onChange={e => { setEmpId(e.target.value); setCurrentStatus(null); }}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder="Scan or type your employee ID"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                autoFocus
              />

              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleLookup()}
                disabled={!empId.trim() || lookupLoading}
                sx={{ mb: 2 }}
              >
                {lookupLoading ? <CircularProgress size={20} /> : 'Look Up'}
              </Button>

              {/* Status display */}
              {currentStatus && (
                <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  {currentStatus.worker_name ? (
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {currentStatus.worker_name}
                    </Typography>
                  ) : (
                    <Alert severity="error" sx={{ mb: 1 }}>Employee not found.</Alert>
                  )}
                  {currentStatus.worker_name && (
                    <>
                      <Box sx={{ mb: 1 }}>{statusChip(status, currentStatus.session?.current_break_type, currentStatus.activity?.category_label)}</Box>
                      {status !== 'clocked_out' && currentStatus.session?.clock_in_at && (
                        <Typography variant="body2" color="text.secondary">
                          In since {formatTime(currentStatus.session.clock_in_at)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* Action buttons */}
              {currentStatus?.worker_name && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {status === 'clocked_out' && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Activity Check In — choose a task:
                      </Typography>
                      <Grid container spacing={1}>
                        {categories.map(cat => (
                          <Grid item xs={6} key={cat.key}>
                            <Button
                              variant="contained"
                              color="success"
                              fullWidth
                              onClick={() => tapCheckIn(cat)}
                              disabled={actionLoading}
                              sx={{ minHeight: 64, fontSize: '0.95rem', fontWeight: 600, textTransform: 'none', lineHeight: 1.2 }}
                            >
                              {cat.label}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                      {categories.length === 0 && (
                        <Alert severity="warning">No activity categories available — contact an admin.</Alert>
                      )}
                    </>
                  )}
                  {status === 'clocked_in' && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary">
                        Switch task:
                      </Typography>
                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        {categories.map(cat => {
                          const isCurrent = currentStatus.activity?.category_key === cat.key;
                          return (
                            <Grid item xs={6} key={cat.key}>
                              <Button
                                variant={isCurrent ? 'contained' : 'outlined'}
                                color={isCurrent ? 'success' : 'primary'}
                                fullWidth
                                onClick={() => tapSwitch(cat)}
                                disabled={actionLoading}
                                sx={{ minHeight: 64, fontSize: '0.95rem', fontWeight: 600, textTransform: 'none', lineHeight: 1.2 }}
                              >
                                {cat.label}
                              </Button>
                            </Grid>
                          );
                        })}
                      </Grid>
                      <Button
                        variant="contained"
                        sx={{ backgroundColor: '#7b1fa2', '&:hover': { backgroundColor: '#6a1b9a' } }}
                        fullWidth
                        startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LunchDiningIcon />}
                        onClick={() => handleAction('break/start', 'lunch')}
                        disabled={actionLoading}
                      >
                        Start Lunch
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <CoffeeIcon />}
                        onClick={() => handleAction('break/start', 'break')}
                        disabled={actionLoading}
                      >
                        Start Break
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LogoutIcon />}
                        onClick={() => handleAction('out')}
                        disabled={actionLoading}
                      >
                        Activity Check Out
                      </Button>
                    </>
                  )}
                  {status === 'on_break' && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : (currentStatus.session?.current_break_type === 'lunch' ? <LunchDiningIcon /> : <CoffeeIcon />)}
                        onClick={() => handleAction('break/end')}
                        disabled={actionLoading}
                      >
                        {currentStatus.session?.current_break_type === 'lunch' ? 'End Lunch' : 'End Break'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <LogoutIcon />}
                        onClick={() => handleAction('out')}
                        disabled={actionLoading}
                      >
                        Activity Check Out
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Paper>

            {/* ── Manager force in/out ──────────────────────────────────── */}
            {canAccessManagerFeatures && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminPanelSettingsIcon /> Manager Override
                </Typography>
                <Autocomplete
                  options={workers}
                  getOptionLabel={(option) => option.name}
                  value={forceWorker}
                  onChange={(e, newValue) => setForceWorker(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.employee_id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Associate" placeholder="Type a name…" />
                  )}
                  sx={{ mb: 2 }}
                />
                <TextField
                  select
                  label="Activity (optional, force-in only)"
                  fullWidth
                  value={forceCategoryKey}
                  onChange={e => setForceCategoryKey(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">No category — associate picks their own</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.key} value={cat.key}>{cat.label}</MenuItem>
                  ))}
                </TextField>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="success"
                    sx={{ flex: 1 }}
                    onClick={() => handleForce('force-in')}
                    disabled={!forceWorker || forceLoading}
                    startIcon={forceLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    Force Activity Check In
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ flex: 1 }}
                    onClick={() => handleForce('force-out')}
                    disabled={!forceWorker || forceLoading}
                    startIcon={forceLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    Force Activity Check Out
                  </Button>
                </Box>
                {/* Contextual lunch/break controls, driven by the selected
                    worker's LIVE roster status */}
                {forceWorker && (() => {
                  const liveRow = roster.find(r => String(r.employee_id) === String(forceWorker.employee_id));
                  if (!liveRow) return null; // checked out → force check-in first (buttons above)
                  if (liveRow.status === 'clocked_in') {
                    return (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          variant="outlined"
                          sx={{ flex: 1, color: '#7b1fa2', borderColor: '#7b1fa2' }}
                          onClick={() => handleForceBreak('lunch')}
                          disabled={forceLoading}
                          startIcon={<LunchDiningIcon />}
                        >
                          Assign Lunch
                        </Button>
                        <Button
                          variant="outlined"
                          color="warning"
                          sx={{ flex: 1 }}
                          onClick={() => handleForceBreak('break')}
                          disabled={forceLoading}
                          startIcon={<CoffeeIcon />}
                        >
                          Assign Break
                        </Button>
                      </Box>
                    );
                  }
                  if (liveRow.status === 'on_break') {
                    return (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={handleForceBreakEnd}
                          disabled={forceLoading}
                          startIcon={liveRow.current_break_type === 'lunch' ? <LunchDiningIcon /> : <CoffeeIcon />}
                        >
                          End Break
                        </Button>
                      </Box>
                    );
                  }
                  return null;
                })()}
                {/* Lead OBD pause/resume — driven by the worker's live OBD state,
                    independent of their clock status. Pause is lead-only now. */}
                {forceWorker && forceWorkerObds.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {forceWorkerObds.map(obd => (
                      <Button
                        key={obd.id}
                        fullWidth
                        variant="outlined"
                        color={obd.status === 'In Progress' ? 'info' : 'primary'}
                        sx={{ mt: 1 }}
                        onClick={() => handleObdPauseResume(obd, obd.status === 'In Progress' ? 'pause' : 'resume')}
                        disabled={forceLoading}
                        startIcon={obd.status === 'In Progress' ? <PauseIcon /> : <PlayArrowIcon />}
                      >
                        {obd.status === 'In Progress'
                          ? `Pause OBD ${obd.obd_number}`
                          : `Resume OBD ${obd.obd_number}`}
                      </Button>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* ── Clock-in reminder pilot toggle (admin only) ───────────────── */}
            {isAdmin && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon /> Check-In Reminder
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={nagEnabled}
                      onChange={e => handleToggleNag(e.target.checked)}
                      disabled={nagSaving}
                    />
                  }
                  label={nagEnabled ? 'Enabled' : 'Disabled'}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  When on, associates who start an OBD without checking in get a dismissible reminder to check in. It never blocks picking — pilot it on a small group first.
                </Typography>
              </Paper>
            )}

            {/* ── Auto check-in on OBD claim toggle (admin only) ────────────── */}
            {isAdmin && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon /> Auto Check-In on OBD Claim
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoPickingEnabled}
                      onChange={e => handleToggleAutoPicking(e.target.checked)}
                      disabled={autoPickingSaving}
                    />
                  }
                  label={autoPickingEnabled ? 'Enabled' : 'Disabled'}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  When on, claiming an OBD automatically checks the associate in under Picking (ending any break or switching from another task). When off, the reminder above is the only prompt. Never blocks picking.
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* ── Live roster ─────────────────────────────────────────────────── */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon /> Live Roster
                  <Chip
                    label={`${roster.length} checked in`}
                    size="small"
                    sx={{ ml: 1, backgroundColor: roster.length > 0 ? '#4caf50' : '#9e9e9e', color: '#fff' }}
                  />
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {canAccessManagerFeatures && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<PowerSettingsNewIcon />}
                      onClick={() => {
                        setClockOutError('');
                        setClockOutPassword('');
                        setClockOutUsername(currentUser?.username || '');
                        setClockOutAllOpen(true);
                      }}
                      disabled={roster.length === 0}
                    >
                      Activity Check Out All
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={fetchRoster} disabled={rosterLoading}>
                    {rosterLoading ? <CircularProgress size={16} /> : 'Refresh'}
                  </Button>
                </Box>
              </Box>

              {roster.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No one is currently checked in.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Activity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Check In</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Elapsed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Net</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Lunch</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Break</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roster.map(row => {
                        const elapsedMs  = Math.max(0, currentTime - new Date(row.clock_in_at));
                        const totalSec   = Math.floor(elapsedMs / 1000);
                        const lunchSec0  = parseFloat(row.lunch_seconds) || 0;
                        const breakSec0  = parseFloat(row.break_seconds) || 0;
                        // If currently on a break/lunch, live-update the matching counter.
                        const onLunch = row.status === 'on_break' && row.current_break_type === 'lunch' && row.current_break_start;
                        const onBreak = row.status === 'on_break' && row.current_break_type !== 'lunch' && row.current_break_start;
                        const liveLunchSec = onLunch
                          ? lunchSec0 + Math.max(0, (currentTime - new Date(row.current_break_start)) / 1000)
                          : lunchSec0;
                        const liveBreakSec = onBreak
                          ? breakSec0 + Math.max(0, (currentTime - new Date(row.current_break_start)) / 1000)
                          : breakSec0;
                        const netSec = Math.max(0, totalSec - Math.floor(liveLunchSec) - Math.floor(liveBreakSec));

                        const fmt = s => {
                          const h = Math.floor(s / 3600);
                          const m = Math.floor((s % 3600) / 60);
                          const sec = s % 60;
                          return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
                        };

                        return (
                          <TableRow
                            key={row.id}
                            sx={{ backgroundColor: row.status === 'on_break' ? (row.current_break_type === 'lunch' ? '#f3e5f5' : '#fff8e1') : 'inherit' }}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{row.worker_name}</TableCell>
                            <TableCell>{statusChip(row.status, row.current_break_type)}</TableCell>
                            <TableCell>
                              {row.current_category_label
                                ? <Chip
                                    label={row.current_vendor_name ? `${row.current_category_label} — ${row.current_vendor_name}` : row.current_category_label}
                                    size="small" variant="outlined" color="primary" sx={{ fontWeight: 600 }} />
                                : '—'}
                            </TableCell>
                            <TableCell>{formatTime(row.clock_in_at)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(totalSec)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(netSec)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: liveLunchSec > 0 ? '#7b1fa2' : 'inherit' }}>
                              {liveLunchSec > 0 ? fmt(Math.floor(liveLunchSec)) : '—'}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: liveBreakSec > 0 ? '#ff9800' : 'inherit' }}>
                              {liveBreakSec > 0 ? fmt(Math.floor(liveBreakSec)) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* ── Reports ─────────────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon /> Activity Report
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                <DateTimePicker
                  label="Start"
                  value={reportStart}
                  onChange={v => isValid(v) && setReportStart(v)}
                  slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
                />
                <DateTimePicker
                  label="End"
                  value={reportEnd}
                  onChange={v => isValid(v) && setReportEnd(v)}
                  slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
                />
                <Button
                  variant="contained"
                  onClick={fetchReport}
                  disabled={reportLoading}
                  startIcon={reportLoading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  Load Report
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportReportCSV}
                  disabled={!reportData || reportData.length === 0}
                >
                  Export CSV
                </Button>
              </Box>

              {reportData === null ? (
                <Typography color="text.secondary">Select a date range and click Load Report.</Typography>
              ) : reportData.length === 0 ? (
                <Typography color="text.secondary">No sessions found for this range.</Typography>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    "Picking time" is time spent completing OBD pick lists (start → finish). "Gap" = net checked-in time minus picking time — visible idle time per Wade's time study. Lunch and break are tracked separately. Auto-closed sessions (idle 4h+) are excluded from these totals.
                  </Alert>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Sessions</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Checked-In</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Lunch</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Break</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Net Checked-In</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Picking Time</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Gap ⚠</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.map(row => (
                          <TableRow key={row.worker_name}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {row.worker_name}
                              {row.auto_closed_count > 0 && (
                                <Chip
                                  label={`${row.auto_closed_count} auto-closed`}
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ ml: 1, height: 18, fontSize: '0.62rem' }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">{row.session_count}</TableCell>
                            <TableCell align="right">{formatMinutes(row.total_clocked_minutes)}</TableCell>
                            <TableCell align="right" sx={{ color: row.lunch_minutes > 0 ? '#7b1fa2' : 'inherit' }}>
                              {formatMinutes(row.lunch_minutes)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: row.break_minutes > 0 ? '#ff9800' : 'inherit' }}>
                              {formatMinutes(row.break_minutes)}
                            </TableCell>
                            <TableCell align="right">{formatMinutes(row.net_clocked_minutes)}</TableCell>
                            <TableCell align="right" sx={{ color: row.picking_minutes > 0 ? '#4caf50' : '#9e9e9e' }}>
                              {row.picking_minutes > 0 ? formatMinutes(row.picking_minutes) : '—'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: row.gap_minutes > 60 ? 700 : 400,
                                color: row.gap_minutes > 60 ? '#d32f2f' : row.gap_minutes > 0 ? '#f57c00' : '#4caf50',
                              }}
                            >
                              {row.picking_minutes > 0 ? formatMinutes(row.gap_minutes) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Totals row — Lunch/Break/Net as % of Total Checked-In;
                            Picking/Gap as % of Net (the two parts of net). */}
                        {(() => {
                          const t = computeReportTotals(reportData, categories);
                          const totalSx = { fontWeight: 700, borderTop: '2px solid #bdbdbd', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap' };
                          return (
                            <TableRow>
                              <TableCell sx={totalSx}>Total</TableCell>
                              <TableCell align="right" sx={totalSx}>{t.sessions}</TableCell>
                              <TableCell align="right" sx={totalSx}>{formatMinutes(t.total)}</TableCell>
                              <TableCell align="right" sx={totalSx}>{withPct(t.lunch, t.total)}</TableCell>
                              <TableCell align="right" sx={totalSx}>{withPct(t.brk, t.total)}</TableCell>
                              <TableCell align="right" sx={totalSx}>{withPct(t.net, t.total)}</TableCell>
                              <TableCell align="right" sx={totalSx}>{withPct(t.picking, t.net)}</TableCell>
                              <TableCell align="right" sx={totalSx}>{withPct(t.gap, t.net)}</TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ⚠ Gap requires OBD picking data for the same period. Rows without picking data show "—". Auto-closed sessions are flagged and excluded from totals.
                  </Typography>

                  {/* Activity breakdown: hours allocated by task category */}
                  {categories.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 700 }}>
                        Activity Breakdown
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                              {categories.map(c => (
                                <TableCell key={c.key} align="right" sx={{ fontWeight: 700 }}>{c.label}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Unallocated</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportData.map(row => (
                              <TableRow key={row.worker_name}>
                                <TableCell sx={{ fontWeight: 600 }}>{row.worker_name}</TableCell>
                                {categories.map(c => {
                                  const min = row.category_minutes?.[c.key] || 0;
                                  return (
                                    <TableCell key={c.key} align="right" sx={{ color: min > 0 ? 'inherit' : '#9e9e9e' }}>
                                      {min > 0 ? formatMinutes(min) : '—'}
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right" sx={{ color: (row.unallocated_minutes || 0) > 0 ? '#f57c00' : '#4caf50', fontWeight: 600 }}>
                                  {formatMinutes(row.unallocated_minutes || 0)}
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Totals row — each column as % of (allocated + unallocated),
                                so the percentages sum to 100% across the row. */}
                            {(() => {
                              const t = computeReportTotals(reportData, categories);
                              const totalSx = { fontWeight: 700, borderTop: '2px solid #bdbdbd', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap' };
                              return (
                                <TableRow>
                                  <TableCell sx={totalSx}>Total</TableCell>
                                  {categories.map(c => {
                                    const min = t.categories[c.key] || 0;
                                    return (
                                      <TableCell key={c.key} align="right" sx={{ ...totalSx, color: min > 0 ? 'inherit' : '#9e9e9e' }}>
                                        {min > 0 ? withPct(min, t.breakdownDenom) : '—'}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell align="right" sx={totalSx}>
                                    {withPct(t.unallocated, t.breakdownDenom)}
                                  </TableCell>
                                </TableRow>
                              );
                            })()}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Unallocated = net checked-in time − time allocated to a task. Auto-closed activity rows are excluded, matching the session math.
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Paper>
          </Grid>

        </Grid>

        {/* Clock Out All — confirmation + credential gate (manager/admin) */}
        <Dialog open={clockOutAllOpen} onClose={() => !clockOutLoading && setClockOutAllOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PowerSettingsNewIcon color="error" /> Activity Check Out — Everyone
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will check out <strong>{roster.length}</strong> associate{roster.length === 1 ? '' : 's'} currently checked in.
              They will each need to check back in before they can start an OBD.
            </Alert>

            {roster.length > 0 && (
              <Box sx={{ mb: 2, maxHeight: 160, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {roster.map(r => (
                  <Typography key={r.id} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <span>{r.worker_name}</span>
                    <span>{statusChip(r.status, r.current_break_type)}</span>
                  </Typography>
                ))}
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter a manager or admin username and password to authorize.
            </Typography>
            <TextField
              label="Manager/Admin Username"
              fullWidth
              value={clockOutUsername}
              onChange={e => setClockOutUsername(e.target.value)}
              sx={{ mb: 2 }}
              autoComplete="off"
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={clockOutPassword}
              onChange={e => setClockOutPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleClockOutAll()}
              autoComplete="off"
            />
            {clockOutError && <Alert severity="error" sx={{ mt: 2 }}>{clockOutError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClockOutAllOpen(false)} disabled={clockOutLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleClockOutAll}
              disabled={clockOutLoading || !clockOutUsername.trim() || !clockOutPassword}
              startIcon={clockOutLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Activity Check Out All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Optional vendor/PO tag for Receiving/Putaway check-ins.
            Skip always proceeds normally with null tags — never blocks. */}
        <Dialog
          open={vendorDialog.open}
          onClose={() => completeVendorDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{vendorDialog.catLabel} — which vendor? (optional)</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If you're receiving a specific truck, tag it here — or just skip.
            </Typography>
            <TextField
              select
              label="Vendor (optional)"
              fullWidth
              value={vendorSel}
              onChange={e => setVendorSel(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">No vendor</MenuItem>
              {vendors.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="PO number (optional)"
              fullWidth
              value={poText}
              onChange={e => setPoText(e.target.value)}
              placeholder="e.g. 4500841043"
            />
          </DialogContent>
          <DialogActions>
            <Button size="large" onClick={() => completeVendorDialog(false)} sx={{ fontWeight: 700 }}>
              Skip
            </Button>
            <Button size="large" variant="contained" onClick={() => completeVendorDialog(true)}>
              Check In
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default ClockIn;
